import React from 'react';

const ResultDetailsModal = ({ details, isOpen, onClose, loading }) => {
    if (!isOpen) return null;

    // ----- SAFE NUMBER CONVERSION -----
    const toNumber = (value) => {
        if (value === null || value === undefined) return null;
        const num = Number(value);
        return isNaN(num) ? null : num;
    };

    const formatScore = (value) => {
        const num = toNumber(value);
        return num !== null ? num.toFixed(1) : '—';
    };

    const formatPercentage = (value) => {
        const num = toNumber(value);
        return num !== null ? Math.round(num) : '—';
    };

    // Format seconds to mm:ss
    const formatTime = (seconds) => {
        const secs = toNumber(seconds);
        if (secs === null || secs < 0) return '—';
        const mins = Math.floor(secs / 60);
        const remainingSecs = secs % 60;
        return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
    };

    // Get CSS class for score badge
    const getScoreBadgeClass = (percentage) => {
        const pct = toNumber(percentage);
        if (pct === null) return 'bg-gray-100 text-gray-800';
        if (pct >= 70) return 'bg-green-100 text-green-800';
        if (pct >= 50) return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    // ----- HTML RENDERER -----
    const HtmlRenderer = ({ html }) => {
        if (!html) return null;
        return <div dangerouslySetInnerHTML={{ __html: html }} className="prose prose-sm max-w-none" />;
    };

    // ----- EXTRACT SELECTED / CORRECT OPTIONS FROM ANSWER REVIEW ITEM -----
    const getSelectedOptions = (item) => {
        if (!item.options || !Array.isArray(item.options)) return [];
        return item.options.filter(opt => opt.is_selected === true);
    };

    const getCorrectOptions = (item) => {
        if (!item.options || !Array.isArray(item.options)) return [];
        return item.options.filter(opt => opt.is_correct === true);
    };

    // ----- FORMAT STUDENT ANSWER (fallback for older data) -----
    const formatStudentAnswer = (answer) => {
        if (answer === null || answer === undefined) return '—';
        if (Array.isArray(answer)) return answer.join(', ');
        return String(answer);
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 transition-opacity duration-300 ease-in-out"
                    aria-hidden="true"
                    onClick={onClose}
                >
                    <div className="absolute inset-0 bg-gray-600 opacity-75 backdrop-blur-sm"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                {/* Modal Panel - significantly larger */}
                <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl lg:max-w-7xl w-full animate-scale-in">

                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-white flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                {details?.cbt_name || 'Test Results'}
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-green-200 transition-colors duration-200"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Body - more height, better spacing */}
                    <div className="bg-white px-6 py-5 max-h-[80vh] overflow-y-auto">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
                            </div>
                        ) : !details ? (
                            <p className="text-center text-gray-500 py-8">No details available</p>
                        ) : (
                            <div className="space-y-8">
                                {/* ---------- OVERALL SCORE CARD ---------- */}
                                {details.total_score !== null && (
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Test completed on</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {new Date(details.submitted_at).toLocaleString('en-US', {
                                                        dateStyle: 'long', timeStyle: 'short'
                                                    })}
                                                </p>
                                            </div>
                                            <div className="mt-3 sm:mt-0 flex items-baseline gap-4">
                                                <div className="text-right">
                                                    <p className="text-xs text-gray-500">Score</p>
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        {formatScore(details.total_score)} / {formatScore(details.max_score)}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-gray-500">Percentage</p>
                                                    <p className={`text-xl font-bold px-3 py-1 rounded-full ${getScoreBadgeClass(details.percentage)}`}>
                                                        {formatPercentage(details.percentage)}%
                                                    </p>
                                                </div>
                                                {details.passed !== null && (
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500">Result</p>
                                                        <p className={`text-sm font-bold px-3 py-1 rounded-full ${
                                                            details.passed ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                                        }`}>
                                                            {details.passed ? 'PASSED' : 'FAILED'}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {details.grade && (
                                            <div className="mt-3 text-sm text-gray-600">
                                                Grade: <span className="font-semibold text-gray-900">{details.grade}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ---------- PERFORMANCE METRICS ---------- */}
                                {details.correct_answers !== null && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-500">Correct</p>
                                            <p className="text-lg font-bold text-green-600">{toNumber(details.correct_answers) ?? '—'}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-500">Wrong</p>
                                            <p className="text-lg font-bold text-red-600">{toNumber(details.wrong_answers) ?? '—'}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-500">Unanswered</p>
                                            <p className="text-lg font-bold text-gray-600">{toNumber(details.unanswered) ?? '—'}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-500">Accuracy</p>
                                            <p className="text-lg font-bold text-blue-600">
                                                {details.accuracy_rate ? `${formatPercentage(details.accuracy_rate)}%` : '—'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* ---------- TIME ANALYSIS ---------- */}
                                {details.time_analysis && (
                                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
                                        <h4 className="text-sm font-semibold text-indigo-800 mb-2 flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Time Analysis
                                        </h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                            <div>
                                                <span className="text-gray-600">Total time</span>
                                                <p className="font-medium text-gray-900">{formatTime(details.time_analysis.total_time_taken)}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Avg per question</span>
                                                <p className="font-medium text-gray-900">{formatTime(details.time_analysis.average_time_per_question)}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Fastest</span>
                                                <p className="font-medium text-gray-900">{formatTime(details.time_analysis.fastest_question_time)}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Slowest</span>
                                                <p className="font-medium text-gray-900">{formatTime(details.time_analysis.slowest_question_time)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ---------- SECTION PERFORMANCE ---------- */}
                                {details.section_performance?.length > 0 && details.section_performance[0].correct !== null && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Performance by Section</h4>
                                        <div className="space-y-2">
                                            {details.section_performance.map((section, idx) => (
                                                <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex justify-between items-center mb-1">
                                                        <span className="text-sm font-medium text-gray-900">{section.section_name}</span>
                                                        <span className="text-xs text-gray-600">
                                                            {toNumber(section.correct) ?? 0} / {toNumber(section.total_questions) ?? 0} correct
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-green-500 h-2 rounded-full"
                                                            style={{ width: `${toNumber(section.accuracy) || 0}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                                        <span>Score: {formatScore(section.total_score)} / {formatScore(section.max_score)}</span>
                                                        <span>{formatPercentage(section.percentage)}%</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ---------- TOPIC PERFORMANCE ---------- */}
                                {details.topic_performance?.length > 0 && details.topic_performance[0].correct !== null && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Topics</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {details.topic_performance.map((topic, idx) => (
                                                <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-200 flex justify-between items-center">
                                                    <span className="text-xs font-medium text-gray-700 truncate">{topic.topic || 'Untitled'}</span>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                        toNumber(topic.percentage) >= 70 ? 'bg-green-100 text-green-800' :
                                                        toNumber(topic.percentage) >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {formatPercentage(topic.percentage)}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ---------- DIFFICULTY BREAKDOWN ---------- */}
                                {details.difficulty_performance?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Difficulty</h4>
                                        <div className="grid grid-cols-3 gap-2">
                                            {details.difficulty_performance.map((diff, idx) => (
                                                <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-200 text-center">
                                                    <span className="capitalize text-xs text-gray-600">{diff.difficulty}</span>
                                                    <p className="text-sm font-bold text-gray-900">
                                                        {diff.correct !== null
                                                            ? `${toNumber(diff.correct)}/${toNumber(diff.total_questions)}`
                                                            : toNumber(diff.total_questions)}
                                                    </p>
                                                    {diff.percentage !== null && (
                                                        <span className="text-xs text-gray-500">{formatPercentage(diff.percentage)}%</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ---------- QUESTION TYPE BREAKDOWN ---------- */}
                                {details.question_type_performance?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Question Types</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {details.question_type_performance.map((qt, idx) => (
                                                <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-200">
                                                    <span className="capitalize text-xs text-gray-600">{qt.question_type.replace('_', ' ')}</span>
                                                    <p className="text-sm font-bold text-gray-900">
                                                        {qt.correct !== null
                                                            ? `${toNumber(qt.correct)}/${toNumber(qt.total_questions)}`
                                                            : toNumber(qt.total_questions)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ---------- ANSWER REVIEW - ENHANCED CARD LIST WITH HTML ---------- */}
                                {details.answers_review && details.answers_review.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Answer Review
                                        </h4>
                                        <div className="space-y-4">
                                            {details.answers_review.map((item, idx) => {
                                                const selectedOptions = getSelectedOptions(item);
                                                const correctOptions = getCorrectOptions(item);
                                                const hasOptions = item.options && item.options.length > 0;
                                                const isCorrect = item.is_correct;
                                                // Determine if we have a correct/selected set to display
                                                const selectedTexts = selectedOptions.map(opt => opt.text).filter(Boolean);
                                                const correctTexts = correctOptions.map(opt => opt.text).filter(Boolean);

                                                return (
                                                    <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow transition">
                                                        {/* Question Header */}
                                                        <div className="flex justify-between items-start gap-2 flex-wrap">
                                                            <div className="flex-1">
                                                                <div className="text-xs text-gray-500 mb-1">Q{idx + 1}</div>
                                                                <div className="prose prose-sm max-w-none text-gray-900 font-medium">
                                                                    <HtmlRenderer html={item.question_text} />
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-end">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                    isCorrect === true ? 'bg-green-100 text-green-800' :
                                                                    isCorrect === false ? 'bg-red-100 text-red-800' :
                                                                    'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                    {isCorrect === true ? 'Correct' : isCorrect === false ? 'Wrong' : 'Not graded'}
                                                                </span>
                                                                <span className="text-xs text-gray-500 mt-1">
                                                                    Score: {formatScore(item.score_obtained)} / {formatScore(item.max_score)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Options with selection/correct indicators */}
                                                        {hasOptions && (
                                                            <div className="mt-3 border-t border-gray-100 pt-3">
                                                                <p className="text-xs font-medium text-gray-600 mb-1">Options:</p>
                                                                <div className="space-y-1.5">
                                                                    {item.options.map((opt, optIdx) => (
                                                                        <div
                                                                            key={optIdx}
                                                                            className={`flex items-start gap-2 p-2 rounded text-sm ${
                                                                                opt.is_selected && opt.is_correct ? 'bg-green-50 border border-green-200' :
                                                                                opt.is_selected && !opt.is_correct ? 'bg-red-50 border border-red-200' :
                                                                                !opt.is_selected && opt.is_correct ? 'bg-blue-50 border border-blue-200' :
                                                                                'bg-gray-50 border border-gray-100'
                                                                            }`}
                                                                        >
                                                                            <div className="flex-shrink-0 mt-0.5">
                                                                                {opt.is_selected && (
                                                                                    <svg className="h-4 w-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                                    </svg>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex-1 prose prose-sm max-w-none">
                                                                                <HtmlRenderer html={opt.text} />
                                                                            </div>
                                                                            {opt.is_correct && (
                                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                    Correct
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Your Answer / Correct Answer summary (fallback if options missing) */}
                                                        {!hasOptions && (
                                                            <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                                                                <div>
                                                                    <span className="text-gray-500">Your answer:</span>
                                                                    <p className="font-mono bg-gray-100 p-1 rounded mt-0.5 break-all">
                                                                        {formatStudentAnswer(item.student_answer)}
                                                                    </p>
                                                                </div>
                                                                {item.correct_answer && (
                                                       <div>
  <span className="text-gray-500">Correct answer:.</span>
  <div
    className="font-mono bg-gray-100 p-1 rounded mt-0.5 break-all"
    dangerouslySetInnerHTML={{ __html: item.correct_answer }}
  />
</div>

                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Explanation */}
                                                        {item.explanation && (
                                                            <div className="mt-3 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                                                <p className="text-xs font-semibold text-yellow-800 mb-1">Explanation</p>
                                                                <div className="prose prose-sm max-w-none text-gray-800">
                                                                    <HtmlRenderer html={item.explanation} />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* ---------- STRONG/WEAK AREAS & RECOMMENDATIONS ---------- */}
                                {details.strong_areas && details.strong_areas.length > 0 && (
                                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                                        <h4 className="text-sm font-semibold text-green-800 mb-1">✅ Strong Areas</h4>
                                        <p className="text-sm text-gray-700">{details.strong_areas.join(', ')}</p>
                                    </div>
                                )}
                                {details.weak_areas && details.weak_areas.length > 0 && (
                                    <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                                        <h4 className="text-sm font-semibold text-yellow-800 mb-1">⚠️ Needs Improvement</h4>
                                        <p className="text-sm text-gray-700">{details.weak_areas.join(', ')}</p>
                                    </div>
                                )}
                                {details.recommendations && details.recommendations.length > 0 && (
                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                        <h4 className="text-sm font-semibold text-blue-800 mb-1">📘 Recommendations</h4>
                                        <ul className="list-disc list-inside text-sm text-gray-700">
                                            {details.recommendations.map((rec, idx) => (
                                                <li key={idx}>{rec}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end">
                        <button
                            type="button"
                            className="inline-flex justify-center items-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9) translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fadeIn 0.3s ease-out;
                }
                .animate-scale-in {
                    animation: scaleIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ResultDetailsModal;

